import { CalendarDate } from "@/components/Calendar";
import { LoadingOverlay } from "@/components/Loading";
import { PopoverDatePicker } from "@/components/PopoverDatePicker";
import { useProcessedQuery } from "@/hooks/useProcessedQuery";
import { Flex, ScrollArea, Table, Text, Title } from "@mantine/core";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

type PredictionRow = {
  day: string; // "YYYY-MM-DD"
  totalEnergy: string;
  predictionTotalEnergy: string;
  carbonEmission: string;
  predictionCarbonEmission: string;
};

const toNum = (v: string) => {
  if (v == null || v === "") return NaN;
  return Number(String(v).replace(/,/g, ""));
};

const computeMape = (
  rows: PredictionRow[],
  pick: (r: PredictionRow) => [actualStr: string, predStr: string],
) => {
  let sum = 0;
  let n = 0;
  for (const r of rows) {
    const [aStr, pStr] = pick(r);
    const a = toNum(aStr);
    const p = toNum(pStr);
    if (!Number.isFinite(a) || !Number.isFinite(p)) continue;
    if (Math.abs(a) < 1e-12) continue; // 분모 0 보호
    const ape = (Math.abs(a - p) / Math.abs(a)) * 100;
    sum += ape;
    n += 1;
  }
  return { mape: n > 0 ? sum / n : null, count: n };
};

export const CertificationReport = () => {
  const [selectedDate, setSelectedDate] = useState<CalendarDate>([new Date(), new Date()]);
  const [selectedMonth, setSelectedMonth] = useState<CalendarDate>([null, null]);
  const [predictionAccuracy, setPredictionAccuracy] = useState<PredictionRow[]>([]);
  const [showRows, setShowRows] = useState(false);
  const {
    dailySource,
    threeMonthPairSource,
    statisticsData,
    loadingState: { isLoadingThreeMonthPairSource, isLoadingStatisticsData },
  } = useProcessedQuery(
    {
      dailySourceEnabled: true,
      threeMonthPairSourceEnabled: false,
      statisticsEnabled: true,
    },
    selectedDate,
    null,
    null,
    selectedMonth,
  );

  const appendRow = useCallback(
    (row: {
      day: string;
      totalEnergy: string;
      predictionTotalEnergy: string;
      carbonEmission: string;
      predictionCarbonEmission: string;
    }) => {
      setPredictionAccuracy((prev) => [row, ...prev.filter((r) => r.day !== row.day)]);
    },
    [],
  );

  // 초기 선택 키 기억(ref)
  const initialKeyRef = useRef<string | null>(null);
  // 마지막으로 반영한 키(중복 append 방지)
  const lastAppliedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const start = selectedDate?.[0];
    if (!start) return;

    const key = dayjs(start).format("YYYY-MM-DD");

    // 최초 마운트: 키만 기록하고 append 스킵
    if (initialKeyRef.current === null) {
      initialKeyRef.current = key; // 초기 자동 조회는 건너뜀
      lastAppliedKeyRef.current = key; // 같은 키로는 append 안 되도록 초기화
      return;
    }

    // 같은 키면 아무 것도 안 함
    if (lastAppliedKeyRef.current === key) return;

    const first = dailySource?.data?.[0];
    if (!first) return;

    appendRow({
      day: key,
      totalEnergy: String(first.totalEnergy ?? ""),
      predictionTotalEnergy: String(first.predictionTotalEnergy ?? ""),
      carbonEmission: String(first.carbonEmission ?? ""),
      predictionCarbonEmission: String(first.predictionCarbonEmission ?? ""),
    });

    lastAppliedKeyRef.current = key; // 최신 키 갱신
  }, [dailySource, selectedDate, appendRow]);

  // 에너지 섹션 MAPE
  const { mape: energyMAPE, count: energyN } = computeMape(predictionAccuracy, (r) => [
    r.totalEnergy,
    r.predictionTotalEnergy,
  ]);

  // 탄소 섹션 MAPE
  const { mape: carbonMAPE, count: carbonN } = computeMape(predictionAccuracy, (r) => [
    r.carbonEmission,
    r.predictionCarbonEmission,
  ]);
  useEffect(() => {
    // selectedMonth가 변경되면 rows를 보여줌
    if (selectedMonth && selectedMonth[0]) {
      setShowRows(true);
    }
  }, [selectedMonth]);
  const months = Array.from(
    new Set(
      statisticsData?.data
        ?.filter((item) => item.year === 2024 || item.year === 2025)
        .map((item) => Number(item.month)),
    ),
  ).sort((a, b) => a - b);

  // 월별 데이터 추출 함수
  const getMonthlyTotal = (year: number, month: number) =>
    statisticsData?.data
      ?.filter((item) => String(item.year) === String(year) && Number(item.month) === month)
      .reduce((acc, cur) => acc + Number(cur.totalEnergy ?? 0), 0) ?? 0;

  const rows = months.map((month) => {
    const total2024 = getMonthlyTotal(2024, month);
    const total2025 = getMonthlyTotal(2025, month);
    const rate =
      total2024 && total2025
        ? (((total2024 - total2025) / total2024) * 100).toLocaleString(undefined, {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          }) + "%"
        : "";
    return { month, total2024, total2025, rate };
  });

  const sum2024 = rows.reduce((acc, cur) => acc + cur.total2024, 0);
  const sum2025 = rows.reduce((acc, cur) => acc + cur.total2025, 0);
  const savingRate =
    sum2024 && sum2025
      ? (((sum2024 - sum2025) / sum2024) * 100).toLocaleString(undefined, {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        }) + "%"
      : "";

  return (
    <Flex w={"100%"} h={"100%"} direction={"column"} gap="xs">
      <Flex w="100%" direction={"column"} p="xs" style={{ border: "1px solid gray" }} gap="xs">
        <Title order={4} color="#03f4c9">
          에너지 소비 예측 정확도
        </Title>
        <PopoverDatePicker date={{ selectedDate, setSelectedDate }} type="default" />

        <Flex gap="sm" align="center" direction={"row"} w="100%">
          <ScrollArea
            h="12rem"
            w="100%"
            type="auto"
            styles={{
              thumb: {
                backgroundColor: "#fff", // 엄지(손잡이)
                borderRadius: 8,
              },
              corner: { backgroundColor: "transparent" }, // 필요 시
            }}
          >
            <Table
              withBorder
              withColumnBorders
              w="100%"
              style={{
                color: "white",
              }}
            >
              <thead>
                <tr>
                  <th style={{ color: "white" }}>일자</th>
                  <th style={{ color: "white" }}>실측값(kWh)</th>
                  <th style={{ color: "white" }}>예측값(kWh)</th>
                  <th style={{ color: "white" }}>절대오차</th>
                  <th style={{ color: "white" }}>백분오차</th>
                </tr>
              </thead>
              <tbody>
                {predictionAccuracy.length > 0 ? (
                  predictionAccuracy.map((item, index) => {
                    const totalEnergyNum = Number(item.totalEnergy);
                    const predictionTotalEnergyNum = Number(item.predictionTotalEnergy);
                    const absErr = Math.abs(totalEnergyNum - predictionTotalEnergyNum);
                    const pctErr =
                      item.totalEnergy === "" || isNaN(totalEnergyNum)
                        ? null
                        : (absErr / totalEnergyNum) * 100;
                    return (
                      <tr key={index}>
                        <td>{dayjs(item.day).format("YYYY-MM-DD")}</td>
                        <td>
                          {totalEnergyNum.toLocaleString(undefined, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                        <td>
                          {predictionTotalEnergyNum.toLocaleString(undefined, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                        <td>
                          {absErr.toLocaleString(undefined, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                        <td>{pctErr == null ? "" : `${pctErr.toFixed(4)}%`}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} />
                  </tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
        </Flex>

        <Flex>
          <Text color="white">
            평균 오차율(MAPE): {energyMAPE == null ? "-" : `${energyMAPE.toFixed(2)}%`}
          </Text>
        </Flex>
      </Flex>
      <Flex w="100%" direction={"column"} p="xs" style={{ border: "1px solid gray" }} gap="xs">
        <Title order={4} color="#03f4c9">
          탄소배출 자동 산정 정확도
        </Title>
        <PopoverDatePicker date={{ selectedDate, setSelectedDate }} type="default" />

        <Flex gap="sm" align="center" direction={"row"}>
          <ScrollArea
            h="12rem"
            w="100%"
            type="auto"
            styles={{
              thumb: {
                backgroundColor: "#fff", // 엄지(손잡이)
                borderRadius: 8,
              },
              corner: { backgroundColor: "transparent" }, // 필요 시
            }}
          >
            <Table withBorder withColumnBorders style={{ color: "white" }}>
              <thead>
                <tr>
                  <th style={{ color: "white" }}>일자</th>
                  <th style={{ color: "white" }}>수동 계산값</th>
                  <th style={{ color: "white" }}>시스템 자동산정값</th>
                  <th style={{ color: "white" }}>절대오차</th>
                  <th style={{ color: "white" }}>백분오차</th>
                </tr>
              </thead>
              <tbody>
                {predictionAccuracy.length > 0 ? (
                  predictionAccuracy.map((item, index) => {
                    const totalCarbonEmissionNum = Number(item.carbonEmission);
                    const predictionTotalCarbonEmissionNum = Number(item.predictionCarbonEmission);
                    const absErr = Math.abs(
                      totalCarbonEmissionNum - predictionTotalCarbonEmissionNum,
                    );
                    const pctErr =
                      item.carbonEmission === "" || isNaN(totalCarbonEmissionNum)
                        ? null
                        : (absErr / totalCarbonEmissionNum) * 100;
                    return (
                      <tr key={index}>
                        <td>{dayjs(item.day).format("YYYY-MM-DD")}</td>
                        <td>
                          {totalCarbonEmissionNum.toLocaleString(undefined, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                        <td>
                          {predictionTotalCarbonEmissionNum.toLocaleString(undefined, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                        <td>
                          {absErr.toLocaleString(undefined, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                        <td>{pctErr == null ? "" : `${pctErr.toFixed(4)}%`}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} />
                  </tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
        </Flex>

        <Flex>
          <Text color="white">
            평균 오차율(MAPE): {carbonMAPE == null ? "-" : `${carbonMAPE.toFixed(2)}%`}
          </Text>
        </Flex>
      </Flex>
      <Flex w="100%" direction={"column"} p="xs" style={{ border: "1px solid gray" }} gap="xs">
        <Title order={4} color="#03f4c9">
          에너지 절감 솔루션 검증
        </Title>
        <PopoverDatePicker
          date={{ selectedDate: selectedMonth, setSelectedDate: setSelectedMonth }}
          defaultLevel="year"
        />
        <Flex gap="sm" align="center" direction={"row"}>
          <Table withBorder withColumnBorders style={{ color: "white" }}>
            <LoadingOverlay isLoading={isLoadingStatisticsData} />
            <thead>
              <tr>
                <th style={{ color: "white" }}>일자</th>
                <th style={{ color: "white" }}>기준선 (전년 평균)</th>
                <th style={{ color: "white" }}>적용 후 (현재 사용량)</th>
                <th style={{ color: "white" }}>절감률</th>
              </tr>
            </thead>
            <tbody>
              {showRows &&
                rows.map((row) => (
                  <tr key={row.month}>
                    <td>{`${row.month}월`}</td>
                    <td>{row.total2024.toLocaleString()}</td>
                    <td>{row.total2025.toLocaleString()}</td>
                    <td>{row.rate}</td>
                  </tr>
                ))}
              {showRows && (
                <tr style={{ fontWeight: "bold", background: "#222" }}>
                  <td>합계</td>
                  <td>{sum2024.toLocaleString()}</td>
                  <td>{sum2025.toLocaleString()}</td>
                  <td>{savingRate}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Flex>
      </Flex>
    </Flex>
  );
};
